import { useCallback } from 'react';
import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

import { type PartialProduct } from '../../graphql';
import { useDeliveryOrderInFlight } from '../useDeliveryOrderInFlight';

// ────────────────────────────────────────────────────────────────────────────────

export const useNavigateToCustomization = (props: Params) => {
  const { restaurantSlug, deliveryAddressId } = props;

  const { track } = useTelemetry();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  // ─── HELPERS ─────────────────────────────────────────────────────

  const sendTelemetryEvent = useCallback(
    (product: PartialProduct) => {
      track('pdp.view', {
        entryPoint: 'menu',
        selectedProduct: telemetryProductFromInput({
          ...product,
          location: restaurantSlug,
        }),
      });
    },
    [restaurantSlug, track],
  );

  const navigateToCustomization = useCallback(
    (productSlug: string) => {
      if (!restaurantSlug) return;

      if (isExpoRouterEnabled) {
        router?.navigate({
          pathname: '/[restaurantSlug]/[productSlug]',
          params: { restaurantSlug, productSlug },
        });
        return;
      }

      navigation?.navigate('Modal', {
        screen: 'Customization',
        params: { productSlug, restaurantSlug },
      });
    },
    [isExpoRouterEnabled, navigation, restaurantSlug, router],
  );

  const navigateToCustomizationDelivery = useCallback(
    (productSlug: string) => {
      if (!deliveryAddressId) return;

      // Block delivery pdp navigation when there is an in-flight delivery order.
      if (inFlightDeliveryOrderId) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      if (isExpoRouterEnabled) {
        router?.navigate({
          pathname: '/delivery/[addressId]/[productSlug]',
          params: { addressId: deliveryAddressId, productSlug },
        });
        return;
      }

      navigation?.navigate('Modal', {
        screen: 'CustomizationDelivery',
        params: {
          productSlug,
          addressId: deliveryAddressId,
        },
      });
    },
    [
      deliveryAddressId,
      inFlightDeliveryOrderId,
      isExpoRouterEnabled,
      navigation,
      navigateToDeliveryOrderInFlightScreen,
      router,
    ],
  );

  // ─────────────────────────────────────────────────────────────────

  return useCallback(
    (product: PartialProduct) => {
      const { slug: productSlug = '' } = product;

      sendTelemetryEvent(product);

      if (deliveryAddressId) {
        navigateToCustomizationDelivery(productSlug);
        return;
      }

      navigateToCustomization(productSlug);
    },
    [
      sendTelemetryEvent,
      deliveryAddressId,
      navigateToCustomizationDelivery,
      navigateToCustomization,
    ],
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type Params = {
  restaurantSlug?: string;
  deliveryAddressId?: string;
};
